import { HirerNotificationPreferenceProvider } from '@seek/hirer-notification-preference';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  Dialog,
  Inline,
  Stack,
  Text,
  useToast,
} from 'braid-design-system';
import { observer } from 'mobx-react';

import {
  EVENT_NAME_MANAGE_NOTIFICATIONS_SELF_CANCEL_PRESSED,
  EVENT_NAME_MANAGE_NOTIFICATIONS_JOB_EXPIRED_RETURN_PRESSED,
} from 'src/constants/pageTrackingData';
import hirerGraphqlClient from 'src/graphql/hirer/client';
import { CalendarIllustration } from 'src/libs/Illustrations/CalendarIllustration';
import { trackEvent } from 'src/modules/events';

import translations from './.vocab';
import { NotificationPreferenceForm } from './NotificationPreferenceForm';

interface Props {
  open: boolean;
  onClose: () => void;
  jobId: string;
  jobTitle: string;
  isJobExpired: boolean;
  seekAdvertiserId: string;
  userId: string;
  email: string;
}

export const NotificationPreferenceModal = ({
  open,
  onClose,
  jobId,
  jobTitle,
  isJobExpired,
  seekAdvertiserId,
  userId,
  email,
}: Props) => {
  const { t } = useTranslations(translations);

  const showToast = useToast();

  const onCancel = () => {
    trackEvent(EVENT_NAME_MANAGE_NOTIFICATIONS_SELF_CANCEL_PRESSED, {});
    onClose();
  };

  const onReturnToJob = () => {
    trackEvent(EVENT_NAME_MANAGE_NOTIFICATIONS_JOB_EXPIRED_RETURN_PRESSED, {});
    onClose();
  };

  const onUpdateSuccess = () => {
    onClose();
    showToast({
      tone: 'positive',
      message: t('Email notifications updated'),
      closeLabel: t('Close'),
    });
  };

  return (
    <Dialog
      title={
        isJobExpired
          ? t('This job is expired')
          : t(
              'Choose how often we notify you about new applications for title',
              {
                title: jobTitle,
              },
            )
      }
      id="notification-preferences-dialog"
      width="medium"
      illustration={isJobExpired ? <CalendarIllustration /> : null}
      open={open}
      closeLabel={t('Close')}
      onClose={onClose}
    >
      {isJobExpired ? (
        <Inline space="none" align="center">
          <Box maxWidth="small">
            <Stack space="large" align="center">
              <Text>
                {t(
                  'This job is no longer live. Notification preferences cannot be changed. You are still able to manage your candidates in candidate management.',
                )}
              </Text>
              <Inline space="small">
                <Button variant="solid" onClick={onReturnToJob}>
                  {t('Return to job')}
                </Button>
              </Inline>
            </Stack>
          </Box>
        </Inline>
      ) : (
        <HirerNotificationPreferenceProvider
          jobId={jobId}
          seekAdvertiserId={seekAdvertiserId}
          currentLoginUserId={userId}
          currentLoginUserEmail={email}
          apolloClient={hirerGraphqlClient}
        >
          <NotificationPreferenceForm
            onSubmitSuccess={onUpdateSuccess}
            onCancel={onCancel}
          />
        </HirerNotificationPreferenceProvider>
      )}
    </Dialog>
  );
};
export default observer(NotificationPreferenceModal);
